import React, { useState } from 'react';
import { useCalculator } from '../../context/CalculatorContext';
import { formatCurrency, convertFromMXN } from '../../utils/formatters';
import AnimatedNumberDisplay from '../ui/AnimatedNumberDisplay';
import { TrendingUp, ChevronLeft, BarChart2, Save, RefreshCw, DollarSign, Zap, Calendar, BarChart3, Target, Award, Shield, Plus, Minus, ArrowRight, Edit, Download, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import ResultsSummary from '../ResultsSummary';
import DynamicChartSystem from '../charts/DynamicChartSystem';
import ExcelTableV5 from '../ExcelTableV5';
import { getCertificatePhasesSummary } from '../../utils/calculations/certificateEvolution';
import { getDetailedCertificateEvolution } from '../../utils/calculations/certificateEvolution';
import DownloadReportButton from '../DownloadReportButton';
import PatrimonyChart from '../charts/PatrimonyChart';
import IncomeChart from '../charts/IncomeChart';
import CashFlowChart from '../charts/CashFlowChart';
import CompoundInterestMultiplier from '../compoundInterest/CompoundInterestMultiplier';
import { compoundMultiplierContent } from '../../data/compoundMultiplierContent';
import { downloadCompoundInterestReport } from '../../utils/compoundInterestUtils';

interface ReinvestmentResultsProps {
  onNext: () => void;
  onPrevious: () => void;
  onReset: () => void;
}

const ReinvestmentResults: React.FC<ReinvestmentResultsProps> = ({ onPrevious, onReset, onNext }) => {
  const { 
    investment, 
    results,
    updateInvestment,
    patrimonyChartVisibility,
    incomeChartVisibility,
    cashFlowChartVisibility,
    togglePatrimonyChartElement,
    toggleIncomeChartElement,
    toggleCashFlowChartElement,
    resetPatrimonyChartVisibility,
    resetIncomeChartVisibility,
    resetCashFlowChartVisibility
  } = useCalculator();
  
  const [showExcelTable, setShowExcelTable] = useState(false);
  const [activeTab, setActiveTab] = useState<'summary' | 'compoundInterest' | 'patrimony' | 'income' | 'cashflow' | 'excel'>('summary');
  const [downloadStatus, setDownloadStatus] = useState<'idle' | 'downloading' | 'complete'>('idle');
  
  // Handlers for increment/decrement buttons
  const handleIncrementCertificates = () => {
    if (investment.initialCertificates < 20) {
      updateInvestment({ initialCertificates: investment.initialCertificates + 1 });
    }
  };

  const handleDecrementCertificates = () => {
    if (investment.initialCertificates > 1) {
      updateInvestment({ initialCertificates: investment.initialCertificates - 1 });
    }
  };

  const handleIncrementYears = () => {
    if (investment.years < 30) {
      updateInvestment({ years: investment.years + 1 });
    }
  };

  const handleDecrementYears = () => {
    if (investment.years > 5) {
      updateInvestment({ years: investment.years - 1 });
    }
  };
  
  if (!results) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-green-200 border-t-green-600 rounded-full animate-spin mx-auto mb-4"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <TrendingUp className="w-8 h-8 text-green-600" />
            </div>
          </div>
          <p className="text-gray-600 font-medium">Calculando resultados...</p>
          <p className="text-gray-500 text-sm mt-2">Procesando datos de reinversión</p>
        </div>
      </div>
    );
  }

  // Get detailed certificate evolution and phases - DYNAMIC based on current investment
  const certificateEvolution = getDetailedCertificateEvolution(investment);
  const phases = getCertificatePhasesSummary(certificateEvolution, investment.years);

  if (showExcelTable) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-6xl mx-auto"
      >
        <div className="bg-white rounded-3xl shadow-xl">
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-8 text-white">
            <div className="flex items-center gap-6">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                <BarChart2 className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Tabla de Evolución Detallada - Con Reinversión</h2>
                <p className="text-green-100">Análisis detallado año por año de tu inversión con reinversión automática</p>
              </div>
            </div>
          </div>
          
          <div className="p-8">
            <ExcelTableV5 
              hideAdditionalOptions={true}
              initialCertificates={investment.initialCertificates} 
              years={investment.years}
            />
            
            <div className="mt-8 flex justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowExcelTable(false)}
                className="px-8 py-3 bg-green-600 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-2"
              >
                <ChevronLeft className="w-5 h-5" />
                <span>Volver a Resultados</span>
              </motion.button>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="max-w-6xl mx-auto"
    >
      <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
        <div className="p-8 space-y-8">
          {/* Tabs */}
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('summary')}
              className={`px-6 py-3 font-medium text-sm ${
                activeTab === 'summary' 
                  ? 'text-green-600 border-b-2 border-green-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Resumen
            </button>
            <button
              onClick={() => setActiveTab('compoundInterest')}
              className={`px-6 py-3 font-medium text-sm ${
                activeTab === 'compoundInterest' 
                  ? 'text-green-600 border-b-2 border-green-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Interés Compuesto Multiplicador
            </button>
            <button
              onClick={() => setActiveTab('patrimony')}
              className={`px-6 py-3 font-medium text-sm ${
                activeTab === 'patrimony' 
                  ? 'text-green-600 border-b-2 border-green-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Patrimonio
            </button>
            <button
              onClick={() => setActiveTab('income')}
              className={`px-6 py-3 font-medium text-sm ${
                activeTab === 'income' 
                  ? 'text-green-600 border-b-2 border-green-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Ingresos
            </button>
            <button
              onClick={() => setActiveTab('cashflow')}
              className={`px-6 py-3 font-medium text-sm ${
                activeTab === 'cashflow' 
                  ? 'text-green-600 border-b-2 border-green-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Flujo
            </button>
            <button
              onClick={() => setShowExcelTable(true)}
              className={`px-6 py-3 font-medium text-sm ${
                activeTab === 'excel' 
                  ? 'text-green-600 border-b-2 border-green-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Tabla Excel
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'summary' && (
            <>
              {/* Datos Rápidos - Edición Directa */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl p-6 border border-blue-200">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-blue-500 rounded-2xl flex items-center justify-center">
                    <Edit className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-blue-800">Datos Rápidos - Edición Directa</h3>
                    <p className="text-sm text-blue-600">Haz clic en cualquier valor para editarlo directamente</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  {/* Certificados */}
                  <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 hover:border-blue-300 transition-colors cursor-pointer group">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Certificados</span>
                      <Edit className="w-4 h-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
                    </div>
                    <div className="text-center">
                      <input
                        type="number"
                        min="1"
                        max="20"
                        value={investment.initialCertificates}
                        onChange={(e) => {
                          const value = parseInt(e.target.value);
                          if (!isNaN(value) && value >= 1 && value <= 20) {
                            updateInvestment({ initialCertificates: value });
                          }
                        }}
                        className="w-full text-center text-2xl font-bold text-blue-700 border-0 focus:ring-0 focus:outline-none bg-transparent"
                      />
                      <div className="text-xs text-gray-500">certificados iniciales</div>
                    </div>
                  </div>

                  {/* Precio Base */}
                  <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 hover:border-green-300 transition-colors cursor-pointer group">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Precio Base</span>
                      <Edit className="w-4 h-4 text-gray-400 group-hover:text-green-500 transition-colors" />
                    </div>
                    <div className="text-center">
                      <input
                        type="number"
                        min="200000"
                        max="300000"
                        step="1000"
                        value={investment.certificateBasePrice}
                        onChange={(e) => {
                          const value = parseInt(e.target.value);
                          if (!isNaN(value) && value >= 200000 && value <= 300000) {
                            updateInvestment({ certificateBasePrice: value });
                          }
                        }}
                        className="w-full text-center text-2xl font-bold text-green-700 border-0 focus:ring-0 focus:outline-none bg-transparent"
                      />
                      <div className="text-xs text-gray-500">por certificado</div>
                    </div>
                  </div>

                  {/* Horizonte */}
                  <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 hover:border-purple-300 transition-colors cursor-pointer group">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Horizonte</span>
                      <Edit className="w-4 h-4 text-gray-400 group-hover:text-purple-500 transition-colors" />
                    </div>
                    <div className="text-center">
                      <input
                        type="number"
                        min="5"
                        max="30"
                        value={investment.years}
                        onChange={(e) => {
                          const value = parseInt(e.target.value);
                          if (!isNaN(value) && value >= 5 && value <= 30) {
                            updateInvestment({ years: value });
                          }
                        }}
                        className="w-full text-center text-2xl font-bold text-purple-700 border-0 focus:ring-0 focus:outline-none bg-transparent"
                      />
                      <div className="text-xs text-gray-500">años</div>
                    </div>
                  </div>

                  {/* Producción */}
                  <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 hover:border-orange-300 transition-colors cursor-pointer group">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Producción</span>
                      <Edit className="w-4 h-4 text-gray-400 group-hover:text-orange-500 transition-colors" />
                    </div>
                    <div className="text-center">
                      <input
                        type="number"
                        min="15000"
                        max="35000"
                        step="500"
                        value={investment.averageProductionPerHectare}
                        onChange={(e) => {
                          const value = parseInt(e.target.value);
                          if (!isNaN(value) && value >= 15000 && value <= 35000) {
                            updateInvestment({ averageProductionPerHectare: value });
                          }
                        }}
                        className="w-full text-center text-2xl font-bold text-orange-700 border-0 focus:ring-0 focus:outline-none bg-transparent"
                      />
                      <div className="text-xs text-gray-500">kg/hectárea</div>
                    </div>
                  </div>

                  {/* Precio/Kg */}
                  <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 hover:border-teal-300 transition-colors cursor-pointer group">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Precio/Kg</span>
                      <Edit className="w-4 h-4 text-gray-400 group-hover:text-teal-500 transition-colors" />
                    </div>
                    <div className="text-center">
                      <input
                        type="number"
                        min="20"
                        max="50"
                        step="1"
                        value={investment.averageSalePricePerKg}
                        onChange={(e) => {
                          const value = parseInt(e.target.value);
                          if (!isNaN(value) && value >= 20 && value <= 50) {
                            updateInvestment({ averageSalePricePerKg: value });
                          }
                        }}
                        className="w-full text-center text-2xl font-bold text-teal-700 border-0 focus:ring-0 focus:outline-none bg-transparent"
                      />
                      <div className="text-xs text-gray-500">MXN/kg</div>
                    </div>
                  </div>
                </div>
                
                {/* Escenarios de Producción */}
                <div className="mt-6">
                  <h4 className="text-sm font-medium text-blue-700 mb-3">Escenarios de Producción</h4>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { label: 'Conservador', production: 25000, price: 30, color: 'blue' },
                      { label: 'Moderado', production: 30000, price: 35, color: 'green' },
                      { label: 'Optimista', production: 35000, price: 38, color: 'purple' }
                    ].map((scenario) => (
                      <button
                        key={scenario.label}
                        onClick={() => updateInvestment({ 
                          averageProductionPerHectare: scenario.production,
                          averageSalePricePerKg: scenario.price
                        })}
                        className={`py-3 px-4 rounded-xl text-sm font-medium transition-all duration-200 ${
                          investment.averageProductionPerHectare === scenario.production && investment.averageSalePricePerKg === scenario.price
                            ? `bg-${scenario.color}-600 text-white shadow-md transform scale-105`
                            : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                        }`}
                        title={`${scenario.label}: ${scenario.production.toLocaleString()} kg/ha × $${scenario.price}/kg = ${((scenario.production * scenario.price * 0.1 * 0.65 / investment.certificateBasePrice) * 100).toFixed(1)}% utilidad anual`}
                      >
                        {scenario.label}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="mt-4 p-4 bg-white rounded-xl shadow-sm">
                  <div className="text-center">
                    <div className="text-sm text-gray-600 mb-1">Inversión Total Actualizada</div>
                    <div className="text-2xl font-bold text-blue-700 relative overflow-hidden">
                      <AnimatedNumberDisplay
                        value={convertFromMXN(investment.initialCertificates * investment.certificateBasePrice, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR)}
                        currencyFormat={investment.currencyFormat}
                        duration={1.2}
                        staggerDelay={0.08}
                      />
                    </div>
                    <div className="text-sm text-blue-600 mt-1">
                      Utilidad anual calculada: {((investment.averageProductionPerHectare * investment.averageSalePricePerKg * 0.1 * 0.65 / investment.certificateBasePrice) * 100).toFixed(1)}%
                    </div>
                  </div>
                </div>
              </div>

              {/* Resultados Finales Cosecha Capital - DYNAMIC */}
              <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-3xl shadow-lg p-6 text-white">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-white/20 rounded-2xl flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold">Resultados Finales - Certificado con Reinversión</h3>
                    <h4 className="text-sm font-semibold text-green-100">{compoundMultiplierContent.title}</h4>
                    <p className="text-xs text-green-100">Al año {investment.years}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-green-100 mb-1">Certificados iniciales</div>
                    <div className="text-2xl font-bold relative overflow-hidden">
                      <AnimatedNumberDisplay
                        value={investment.initialCertificates}
                        currencyFormat="MXN"
                        duration={1.0}
                        staggerDelay={0.05}
                        isCurrency={false}
                      />
                    </div>
                  </div>

                  <div>
                    <div className="text-xs text-green-100 mb-1">Por reinversión</div>
                    <div className="text-2xl font-bold relative overflow-hidden">
                      +<AnimatedNumberDisplay
                        value={results.certificatesSummary.fromReinvestment}
                        currencyFormat="MXN"
                        duration={1.2}
                        staggerDelay={0.08}
                        isCurrency={false}
                      />
                    </div>
                  </div>

                  <div>
                    <div className="text-xs text-green-100 mb-1">Certificados produciendo</div>
                    <div className="text-2xl font-bold relative overflow-hidden">
                      <AnimatedNumberDisplay
                        value={phases.producing}
                        currencyFormat="MXN"
                        duration={1.0}
                        staggerDelay={0.06}
                        isCurrency={false}
                      />
                    </div>
                  </div>

                  <div>
                    <div className="text-xs text-green-100 mb-1">En maduración</div>
                    <div className="text-2xl font-bold relative overflow-hidden">
                      <AnimatedNumberDisplay
                        value={phases.waiting + phases.reserved}
                        currencyFormat="MXN"
                        duration={1.0}
                        staggerDelay={0.06}
                        isCurrency={false}
                      />
                    </div>
                  </div>

                  <div>
                    <div className="text-xs text-green-100 mb-1">Ingreso mensual futuro</div>
                    <div className="text-xl font-bold relative overflow-hidden">
                      <AnimatedNumberDisplay
                        value={results.finalMonthlyIncome}
                        currencyFormat={investment.currencyFormat}
                        duration={1.4}
                        staggerDelay={0.1}
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-3 border-t border-green-400">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="text-xs text-green-100 mb-1">Total de Certificados</div>
                      <div className="text-4xl font-extrabold relative overflow-hidden">
                        <AnimatedNumberDisplay
                          value={results.certificatesSummary.totalCertificates}
                          currencyFormat="MXN"
                          duration={1.6}
                          staggerDelay={0.12}
                          isCurrency={false}
                        />
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <div className="text-xs text-green-100 mb-1">Patrimonio final</div>
                      <div className="text-3xl font-bold relative overflow-hidden">
                        <AnimatedNumberDisplay
                          value={results.finalPatrimony}
                          currencyFormat={investment.currencyFormat}
                          duration={1.6}
                          staggerDelay={0.12}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Multiplicador™ Highlight */}
                <div className="mt-4 p-4 bg-white/20 backdrop-blur-sm rounded-xl">
                  <div className="text-center">
                    <div className="text-sm font-bold text-white mb-2">🚀 Poder del Multiplicador™</div>
                    <div className="grid grid-cols-3 gap-4 text-xs">
                      <div>
                        <div className="text-green-100">Multiplicador de Activos</div>
                        <div className="text-lg font-bold text-white">
                          {(results.certificatesSummary.totalCertificates / investment.initialCertificates).toFixed(1)}x
                        </div>
                      </div>
                      <div>
                        <div className="text-green-100">Multiplicador de Patrimonio</div>
                        <div className="text-lg font-bold text-white">
                          {results.capitalMultiplier.toFixed(1)}x
                        </div>
                      </div>
                      <div>
                        <div className="text-green-100">Nuevas Fuentes de Ingresos</div>
                        <div className="text-lg font-bold text-white">
                          +{results.certificatesSummary.fromReinvestment}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Métricas Detalladas */}
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
                <ResultsSummary />
              </div>
              
              {/* Excel Table Button */}
              <div className="flex justify-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowExcelTable(true)}
                  className="px-8 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-2"
                >
                  <BarChart2 className="w-5 h-5" />
                  <span>Ver Tabla de Evolución Detallada</span>
                </motion.button>
              </div>
              
              {/* Download Report Button */}
              <DownloadReportButton />
              
              {/* Reporte Multiplicador™ PDF */}
              <div className="flex justify-center">
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setDownloadStatus('downloading');
                      try {
                        downloadCompoundInterestReport(investment, results, 'html');
                        setTimeout(() => {
                          setDownloadStatus('complete');
                          setTimeout(() => setDownloadStatus('idle'), 2000);
                        }, 1000);
                      } catch (error) {
                        console.error('Error downloading report:', error);
                        setDownloadStatus('idle');
                      }
                    }}
                    disabled={downloadStatus === 'downloading'}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                  >
                    <Download className="w-5 h-5" />
                    <span>Interés Compuesto Multiplicador HTML</span>
                  </button>
                  <button
                    onClick={() => {
                      setDownloadStatus('downloading');
                      try {
                        downloadCompoundInterestReport(investment, results, 'pdf');
                        setTimeout(() => {
                          setDownloadStatus('complete');
                          setTimeout(() => setDownloadStatus('idle'), 2000);
                        }, 1000);
                      } catch (error) {
                        console.error('Error downloading report:', error);
                        setDownloadStatus('idle');
                      }
                    }}
                    disabled={downloadStatus === 'downloading'}
                    className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                  >
                    <Download className="w-5 h-5" />
                    <span>Interés Compuesto Multiplicador PDF</span>
                  </button>
                </div>
                
                {downloadStatus === 'downloading' && (
                  <div className="mt-3 flex items-center justify-center gap-2 text-blue-600">
                    <div className="w-4 h-4 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
                    <span className="text-sm">Generando reporte...</span>
                  </div>
                )}
                
                {downloadStatus === 'complete' && (
                  <div className="mt-3 flex items-center justify-center gap-2 text-green-600">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm">¡Reporte descargado exitosamente!</span>
                  </div>
                )}
              </div>
            </>
          )}

          {activeTab === 'compoundInterest' && (
            <div className="min-h-[500px]">
              <CompoundInterestMultiplier />
            </div>
          )}

          {activeTab === 'patrimony' && (
            <div className="min-h-[500px]">
              <PatrimonyChart />
            </div>
          )}

          {activeTab === 'income' && (
            <div className="min-h-[500px]">
              <IncomeChart />
            </div>
          )}

          {activeTab === 'cashflow' && (
            <div className="min-h-[500px]">
              <CashFlowChart />
            </div>
          )}
          
          {/* Navigation Buttons */}
          <div className="flex justify-between">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onPrevious}
              className="px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-all duration-300 flex items-center gap-2"
            >
              <ChevronLeft className="w-5 h-5" />
              <span>Anterior</span>
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onReset}
              className="px-8 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-2"
            >
              <RefreshCw className="w-5 h-5" />
              <span>Comenzar de Nuevo</span>
            </motion.button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ReinvestmentResults;